import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppLayout from '../../../component/appLayout';
import { ActionBlock, Empty, HeaderBlock, IconDiv, LeftBlock, TitleDiv } from './style';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import DisplayFilterDropdown from '../../../component/displayFilterDropdown/displayFilterDropdown';
import { rootStore } from '../../../mobx/rootStore';
import { Dropdown, DropdownItem } from '../../../component/Dropdown';
import { useLocation, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import history from '../../../history';
import DeleteModal from '../../../component/models/deleteModel';
import ModalCustom from '../../../component/models/modal';
import ViewCard from '../../../component/viewCard/viewCard';
import TaskGrid from '../../project/details/tasks/taskGrid';
import { ICON_COLORS, ICON_TYPE, TASK_LIST_TYPE } from '../../../global/constants';
import TaskPage from '../../project/details/tasks/taskPage';
import EmptyState from '../../../component/emptyState';
import myTaskEmpty from '../../../assets/images/emptystates/lightMyTaskEmpty.svg';
import darkMyTaskEmpty from '../../../assets/images/emptystates/darkMyTaskEmpty.svg';
import { useSelector } from 'react-redux';
import { RootReducerInterface } from '../../../interfaces/RootReducerInterface';
import ViewIcon from '../../../component/ViewIcon';
import * as LucideIcons from 'lucide-react';

interface ViewParams {
  viewId: string;
}

const ViewDetails = () => {
  //Mobx stores variables
  const { viewStore, workspaceStore } = rootStore;
  const { currentView, createViewInput, viewTasks } = viewStore;
  const { currentWorkspace } = workspaceStore;

  //use selector state variables
  const stateSelector = useSelector((state: RootReducerInterface) => state);
  const { settings: settingsSelector } = stateSelector || {};
  const { themeMode } = settingsSelector;

  //other variables
  const { viewId } = useParams<ViewParams>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const location = useLocation();
  const getImageSource = useMemo(() => (themeMode?.theme === 'dark' ? darkMyTaskEmpty : myTaskEmpty), [themeMode]);

  const isEdit = useMemo(
    () => new URLSearchParams(location.search).get('isEdit') === 'true' || viewId === 'new-view',
    [location.search, viewId]
  );

  const loadView = useCallback(async () => {
    if (!viewId || !currentWorkspace?.id || viewId === 'new-view') return;
    try {
      viewStore.setCurrentView(viewId);
    } catch (err) {
      console.log('error', err);
    }
  }, [viewId, currentWorkspace?.id, viewStore]);

  useEffect(() => {
    loadView();
  }, [loadView]);

  const onEditClick = useCallback(() => {
    history.push(`/workspace/${currentWorkspace?.id}/view/${viewId}?isEdit=true`);
  }, [currentWorkspace?.id, viewId]);

  const onDeleteClick = useCallback((e?: React.MouseEvent<Element, MouseEvent>) => {
    if (e) {
      e.stopPropagation();
      setShowDeleteModal(true);
    }
  }, []);

  const dropdownItems = [
    { label: 'Edit', iconName: 'edit-icon', onClick: onEditClick },
    { label: 'Delete', iconName: 'more-menu-delete-icon', onClick: onDeleteClick }
  ];

  const onClickDropdownItem = (label: string) => {
    if (label === 'Delete') {
      setShowDeleteModal(true);
    } else if (label === 'Edit') {
      onEditClick();
    }
  };

  const handleDeleteConfirm = useCallback(async () => {
    // TODO: Implement actual delete logic here
    setShowDeleteModal(false);
  }, [setShowDeleteModal]);

  const handleCloseModal = useCallback(() => {
    setShowDeleteModal(false);
  }, [setShowDeleteModal]);

  const isEmpty = useMemo(() => {
    // Check if all tasks in filteredTasks are empty
    const isFilteredTasksEmpty = viewTasks.filteredTasks.every(
      (task) =>
        (!task.tasks?.length || task.tasks.length === 0) &&
        (!task.completedTasks?.length || task.completedTasks.length === 0)
    );

    // Check if otherTasks are empty
    const isOtherTasksEmpty =
      (!viewTasks.otherTasks?.tasks?.length || viewTasks.otherTasks.tasks.length === 0) &&
      (!viewTasks.otherTasks?.completedTasks?.length || viewTasks.otherTasks.completedTasks.length === 0);

    // Return true only if both filteredTasks and otherTasks are empty
    return isFilteredTasksEmpty && isOtherTasksEmpty;
  }, [viewTasks]);

  const getTaskView = useMemo(() => {
    // if (loading) return null;
    const view = createViewInput?.id
      ? createViewInput?.displayFilters?.taskViewType
      : currentView?.displayFilters?.taskViewType;

    if (isEmpty) {
      return (
        <Empty>
          <EmptyState header={'No tasks matching the filters'} width={330} hideButton={true} image={getImageSource} />
        </Empty>
      );
    }

    if (view === TASK_LIST_TYPE.TASK_GRID) {
      return <TaskGrid loadData={loadView} isView={true} />;
    }
    // if (currentView?.displayFilters?.taskViewType === TASK_LIST_TYPE.CALANDAR_VIEW) {
    //   return (
    //     <CalendarView
    //       loadData={() => {}}
    //       weekWiseTasks={[]}
    //       loadWeekWiseTasks={() => {}}
    //       currentDate={new Date()}
    //       // isView={true}
    //     />
    //   );
    // }
    return <TaskPage loadData={loadView} propsLoading={false} isView={true} isviewdetailPadding={true} />;
  }, [
    createViewInput?.displayFilters?.taskViewType,
    createViewInput?.id,
    currentView?.displayFilters?.taskViewType,
    getImageSource,
    loadView,
    isEmpty
  ]);

  // Get all Lucide icons
  const allIcons = useMemo(() => {
    return Object.entries(LucideIcons)
      .filter(([name]) => /^[A-Z].*Icon$/.test(name))
      .map(([name, component]) => ({
        name,
        displayName: name
          .replace(/([A-Z])/g, ' $1')
          .replace(/^ /, '')
          .replace(/ Icon$/, ''),
        component: component as React.ComponentType<{ size?: number }>
      }));
  }, []);

  // Get the selected icon component
  const SelectedIconComponent = useMemo(() => {
    const defaulIcon = allIcons[0];
    if (!currentView?.icon?.image) return defaulIcon.component;
    const found = allIcons.find((icon) => icon.name === currentView.icon?.image);
    return found ? found.component : defaulIcon.component;
  }, [currentView?.icon?.image, allIcons]);

  return (
    <AppLayout isNewDocPage={true} isPosition={true}>
      {isEdit ? (
        <ViewCard />
      ) : (
        <>
          <HeaderBlock>
            <LeftBlock>
              <TitleDiv>
                <IconDiv onClick={() => history.push('/views')}>
                  <SVGIcon name='view-back-icon' width='16' height='16' viewBox='0 0 16 16' />
                </IconDiv>
                {currentView?.icon?.type === ICON_TYPE.ICON ? (
                  <ViewIcon
                    size='small'
                    backgroundColor={
                      currentView?.color ? ICON_COLORS[currentView?.color as keyof typeof ICON_COLORS] : undefined
                    }>
                    <SelectedIconComponent size={16} />
                  </ViewIcon>
                ) : (
                  <span style={{ fontSize: 16 }}>{currentView?.icon.image}</span>
                )}
                <p>{currentView?.name}</p>
              </TitleDiv>
            </LeftBlock>
            {/* <ViewCard /> */}
            <ActionBlock>
              <DisplayFilterDropdown />
              <Dropdown
                content={
                  <IconDiv>
                    <SVGIcon name='task-detail-dot-icon' width='16' height='16' viewBox='0 0 16 16' className='icon' />
                  </IconDiv>
                }
                trigger='click'>
                {dropdownItems.map((item) => (
                  <DropdownItem
                    key={item.label}
                    label={item.label}
                    iconName={item.iconName}
                    iconSize={16}
                    tone={item.label === 'Delete' ? 'critical' : 'primary'}
                    iconViewBox={item.label === 'Delete' ? '0 0 18 18' : '0 0 16 16'}
                    onClick={() => onClickDropdownItem(item.label)}
                  />
                ))}
              </Dropdown>
            </ActionBlock>
          </HeaderBlock>
        </>
      )}
      {getTaskView}
      <ModalCustom open={showDeleteModal} onClose={handleCloseModal} width={334}>
        <DeleteModal
          modalTitle='Delete View?'
          description='Are you sure you want to delete this view?'
          onClose={handleCloseModal}
          onOk={handleDeleteConfirm}
          buttonText='Delete'
          isPrimaryButton={false}
          isYes={true}
        />
      </ModalCustom>
    </AppLayout>
  );
};

export default observer(ViewDetails);
